/*
 * BM promise-cards mobile-only Swiper.
 *
 * Mirrors the homepage `bm-pathway-cards` pattern (see assets/card-images.js):
 *   - matchMedia-gated so Swiper never initialises on desktop/tablet (≥990px)
 *   - captures the original `.promise-cards__grid` className before any
 *     mutation; `destroySlider()` restores it on unmatch so desktop flex-row
 *     layout is guaranteed identical to the unenhanced HTML
 *   - `loop: false` to avoid the loop-clone × flex-row duplication bug we
 *     hit on homepage image-cards previously
 *
 * Desktop (>=990px) and tablet (768-989px): JS only runs `destroySlider()`,
 * which is a no-op when Swiper was never initialised — the existing
 * `.promise-cards__grid` flex layout in the section stylesheet governs.
 */
if (!customElements.get('promise-cards')) {
  class PromiseCards extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.promise-cards__grid',
        pagination: '.promise-cards__pagination',
        slide: '.promise-cards__card',
      };
      this.classes = {
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
        swiperSlide: 'swiper-slide',
      };

      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      if (!this.sliderWrapper) return;

      this._sliderWrapperDefaultClass = this.sliderWrapper.className;
      this.sliderInstance = false;

      this.mobileMediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      this.handleSliderMode = this.handleSliderMode.bind(this);
      this.mobileMediaQuery.addEventListener('change', this.handleSliderMode);
      this.handleSliderMode();
    }

    handleSliderMode() {
      if (!this.mobileMediaQuery) return;

      if (this.mobileMediaQuery.matches) {
        this.init();
      } else {
        this.destroySlider();
      }
    }

    init() {
      if (typeof this.sliderInstance === 'object') return;

      const paginationEl = this.querySelector(this.selectors.pagination);

      const sliderOptions = {
        slidesPerView: 'auto',
        spaceBetween: 10,
        /*
         * `centeredSlides: true` mirrors the Featured Collection mobile peek
         * so the active card sits centred with equal left/right peek of its
         * neighbours. Safe to leave unguarded here because `init()` is only
         * called when `mobileMediaQuery.matches` is true — desktop destroys
         * the slider via `destroySlider()` in `handleSliderMode()`.
         */
        centeredSlides: true,
        loop: false,
        threshold: 2,
        autoplay: {
          delay: 4000,
          disableOnInteraction: false,
        },
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
            }
          : false,
      };

      const extraModules = window.FoxTheme && window.FoxTheme.Swiper && window.FoxTheme.Swiper.Autoplay
        ? [window.FoxTheme.Swiper.Autoplay]
        : null;

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.add(this.classes.swiperWrapper);
      this.sliderWrapper.querySelectorAll(this.selectors.slide).forEach((card) => {
        card.classList.add(this.classes.swiperSlide);
      });

      try {
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions, extraModules);
        this.sliderInstance.init();
      } catch (error) {
        console.error('[promise-cards] Swiper init failed', {
          section: this.closest('[data-section-id]') && this.closest('[data-section-id]').dataset.sectionId,
          error,
        });
        this.sliderInstance = false;
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);

      if (typeof this.sliderInstance === 'object') {
        try {
          this.sliderInstance.slider.destroy(true, true);
        } catch (error) {
          try {
            this.sliderInstance.slider.destroy();
          } catch (innerError) {
            console.error('[promise-cards] Swiper destroy failed', {
              error: innerError,
              first_error: error,
            });
          }
        }
        this.sliderInstance = false;
      }

      if (!this.sliderWrapper) return;

      this.sliderWrapper.querySelectorAll(this.selectors.slide).forEach((card) => {
        card.classList.remove(this.classes.swiperSlide);
      });

      if (this._sliderWrapperDefaultClass) {
        this.sliderWrapper.className = this._sliderWrapperDefaultClass;
      } else {
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      }
    }

    disconnectedCallback() {
      if (this.mobileMediaQuery && this.handleSliderMode) {
        this.mobileMediaQuery.removeEventListener('change', this.handleSliderMode);
      }
      this.destroySlider();
    }
  }

  customElements.define('promise-cards', PromiseCards);
}
